import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ColumnSettingsContext } from '../../../services/columnSettings/columnSettings';
import {
  ColumnScope,
  columnScreenSize,
  TrackProperty,
} from '../../../services/columnSettings/playlistColumns';
import { ScreenDetectionContext } from '../../../services/screenDetection/screenDetection';
import { useDeductions } from '../../playlist/playlistCommon';

/**
 * Ties a scope's column layout to the element it is shown in.
 *
 * The main viewport's width, less the play button, the context menu button
 * and the scrollbar, is what the columns have to fill. This reports that
 * width to the column settings service whenever it changes - a window
 * resize, a sidebar opening, a layout shift - and the service fits the
 * saved layout to it (see fitColumns). What comes back through the
 * context is already the layout to draw, so the total content width here
 * is a plain sum of it.
 *
 * A ResizeObserver rather than the window's resize event: the viewport
 * changes width for reasons other than the window, and the observer
 * reports once per frame, which is as live as the columns can be drawn
 * anyway. No debounce, and nothing is written to storage on the way - the
 * fit is derived, and only what the user does gets saved.
 */
export default function useDeductedContentWidth(scope: ColumnScope) {
  const [mainViewport, setMainViewport] = useState<HTMLElement>();
  const colCtx = useContext(ColumnSettingsContext);
  const screenSize = columnScreenSize(useContext(ScreenDetectionContext));

  const deductions = useDeductions();

  const columnSettings = colCtx.columnSettingsStore[scope][screenSize];

  const { setViewportWidth, initializeScope } = colCtx;

  useEffect(
    function reportViewportWidth() {
      if (!mainViewport) {
        return;
      }

      const report = () =>
        setViewportWidth(scope, mainViewport.getBoundingClientRect().width - deductions);

      report();

      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(report);

        observer.observe(mainViewport);

        return () => observer.disconnect();
      }

      window.addEventListener('resize', report);

      return () => window.removeEventListener('resize', report);
    },
    [mainViewport, deductions, scope, setViewportWidth],
  );

  const resetColumnWidths = useCallback(
    () =>
      mainViewport &&
      initializeScope(mainViewport.getBoundingClientRect().width - deductions, scope),
    [initializeScope, scope, deductions, mainViewport],
  );

  /**
   * A scope shown for the first time has no layout; it gets the defaults
   * for the viewport it appears in.
   */
  useEffect(
    function initializeOnFirstShow() {
      if (mainViewport && columnSettings === undefined) {
        console.debug('resetting column widths');
        resetColumnWidths();
      }
    },
    [mainViewport, columnSettings, resetColumnWidths],
  );

  /**
   * Total width of the columns as shown.
   */
  const contentWidth = useMemo(() => {
    let total = 0;

    for (const col in columnSettings) {
      total += columnSettings[col as TrackProperty]!.width;
    }

    return total;
  }, [columnSettings]);

  const columnAutoResize = useCallback(
    function columnAutoResize(ev: React.MouseEvent<HTMLDivElement>) {
      if (mainViewport) {
        const parentEl = ev.currentTarget.parentElement!;
        const columnName = parentEl.attributes.getNamedItem('data-column-name')
          ?.value as TrackProperty;

        colCtx.columnAutoResize(
          columnName,
          mainViewport.getBoundingClientRect().width - deductions,
          scope,
          colCtx.minimums[scope]?.[columnName] || 0,
        );
      }
    },
    [mainViewport, colCtx, scope, deductions],
  );

  const hideColumn = useCallback(
    function (columnName: TrackProperty) {
      colCtx.setColumnWidth(columnName, 0, scope);
    },
    [colCtx, scope],
  );

  const unhideColumn = useCallback(
    function (columnName: TrackProperty) {
      if (mainViewport) {
        colCtx.columnAutoResize(
          columnName,
          mainViewport.getBoundingClientRect().width - deductions,
          scope,
          colCtx.minimums[scope]?.[columnName] || 0,
        );
      }
    },
    [colCtx, mainViewport, scope, deductions],
  );

  /**
   * contentWidth + deductions.
   */
  const totalContentWidth = contentWidth + deductions;

  return {
    totalContentWidth,
    mainViewport,
    setMainViewport,
    resetColumnWidths,
    columnAutoResize,
    hideColumn,
    unhideColumn,
  };
}
